import { z } from "zod";
export const EEX_MARKET_DATA_PAGE_LIMIT_MAX = 2_000;
export const EEX_MARKET_DATA_HISTORY_LIMIT_MAX = 500;
export const EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX = 64;
export const EEX_MARKET_DATA_FORWARD_CURVE_SNAPSHOT_MAX = 6;
const NonEmptyString = z.string().min(1);
const NullableString = z.string().nullable();
const NonNegativeInteger = z.number().int().nonnegative();
const ApiFamily = z.enum(["derivatives", "gas_spot", "index"]);
const MarketFamily = z.enum(["derivatives", "gas_spot"]);
const Page = z.object({
    limit: z.number().int().min(1).max(EEX_MARKET_DATA_PAGE_LIMIT_MAX),
    offset: NonNegativeInteger,
    nextOffset: NonNegativeInteger.nullable(),
}).strict();
const ProductGroup = z.object({
    key: NonEmptyString,
    label: NonEmptyString,
    commodityCode: NonEmptyString,
    areaCode: NonEmptyString,
    profile: NullableString,
    familyCode: NullableString,
    currency: NullableString,
    uom: NullableString,
}).strict();
const DeliveryPeriod = z.object({
    key: NonEmptyString,
    kind: z.enum(["day", "weekend", "week", "month", "quarter", "season", "year", "spread"]),
    label: NonEmptyString,
    sortKey: NonEmptyString,
    deliveryStart: NullableString,
    deliveryEnd: NullableString,
}).strict();
const CommonCachedQuery = z.object({
    scopeId: z.string().uuid().optional(),
    apiFamily: ApiFamily.optional(),
    marketSegment: z.string().trim().min(1).max(80).optional(),
    commodityCode: z.string().trim().min(1).max(80).optional(),
    areaCode: z.string().trim().min(1).max(80).optional(),
    dataset: z.string().trim().min(1).max(80).optional(),
});
const PageQuery = z.object({
    limit: z.coerce.number().int().min(1).max(EEX_MARKET_DATA_PAGE_LIMIT_MAX).default(100),
    offset: z.coerce.number().int().min(0).max(1_000_000).default(0),
});
const HistoryPageQuery = PageQuery.extend({
    limit: z.coerce.number().int().min(1).max(EEX_MARKET_DATA_HISTORY_LIMIT_MAX).default(200),
});
const OptionalWindowQuery = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
});
export const EexMarketDataFeedHealthQuerySchema = CommonCachedQuery;
export const EexDegradedTopOfBookRetryRequestSchema = z.object({
    // Omit the list to let the server re-evaluate the current company-scoped
    // eligibility at the instant the administrator requests a retry. This keeps
    // the recovery control usable if a catalogue/history read is delayed.
    scopeIds: z.array(z.string().uuid()).max(24).optional().default([]),
}).strict();
export const EexDegradedTopOfBookRetryResponseSchema = z.object({
    status: z.literal("queued"),
    scopes: z.array(z.object({
        scopeId: z.string().uuid(),
        coalesced: z.boolean(),
    }).strict()),
}).strict();
export const EexDegradedTopOfBookRetryableScopesResponseSchema = z.object({
    scopeIds: z.array(z.string().uuid()),
}).strict();
export const EexMarketDataInstrumentQuerySchema = CommonCachedQuery.merge(PageQuery).extend({
    search: z.string().trim().min(1).max(120).optional(),
    active: z.enum(["true", "false"]).transform((value) => value === "true").optional(),
    matrixEligible: z.enum(["true", "false"]).transform((value) => value === "true").optional(),
    companyUniverseSelected: z.enum(["true", "false"])
        .transform((value) => value === "true").optional(),
});
export const EexMarketDataTopOfBookQuerySchema = CommonCachedQuery.merge(PageQuery).extend({
    subjectId: z.string().uuid().optional(),
    freshnessState: z.enum(["fresh", "stale", "disconnected"]).optional(),
    coverageState: z.enum(["unknown", "quoted", "unquoted", "omitted"]).optional(),
    companyUniverseSelected: z.enum(["true", "false"])
        .transform((value) => value === "true").optional(),
});
export const EexMarketDataPublicTradesQuerySchema = CommonCachedQuery
    .merge(HistoryPageQuery)
    .merge(OptionalWindowQuery)
    .extend({
    subjectId: z.string().uuid().optional(),
    mode: z.enum(["current", "events"]).default("current"),
    companyUniverseSelected: z.enum(["true", "false"])
        .transform((value) => value === "true").optional(),
});
export const EexMarketDataSettlementMarksQuerySchema = CommonCachedQuery
    .omit({ dataset: true })
    .merge(PageQuery)
    .extend({
    companyUniverseSelected: z.enum(["true", "false"])
        .transform((value) => value === "true").optional(),
});
const ForwardCurveSubject = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
}).strict();
const ForwardCurveSnapshotComparison = z.object({
    id: z.string().trim().min(1).max(40).regex(/^[a-z0-9][a-z0-9_-]*$/),
    requestedAt: z.string().datetime({ offset: true }),
}).strict();
/**
 * A bounded read request for historical overlays on a catalogue-defined curve.
 * The caller supplies the exact scope/subject pairs already selected for each
 * delivery-period cell; Brain revalidates every pair against tenant and exact
 * company-universe access. Generic history rollout remains separate; this
 * bounded read may also use rows already retained for a company-selected
 * top-of-book subject.
 */
export const EexMarketDataForwardCurveRequestSchema = z.object({
    subjects: z.array(ForwardCurveSubject)
        .min(1)
        .max(EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX),
    snapshotComparisons: z.array(ForwardCurveSnapshotComparison)
        .max(EEX_MARKET_DATA_FORWARD_CURVE_SNAPSHOT_MAX),
}).strict().superRefine((value, ctx) => {
    const subjectKeys = new Set();
    value.subjects.forEach((subject, index) => {
        const key = `${subject.scopeId}\u0000${subject.subjectId}`;
        if (subjectKeys.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["subjects", index],
                message: "duplicate curve subject",
            });
        }
        subjectKeys.add(key);
    });
    const comparisonIds = new Set();
    value.snapshotComparisons.forEach((comparison, index) => {
        if (comparisonIds.has(comparison.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["snapshotComparisons", index, "id"],
                message: "duplicate snapshot comparison id",
            });
        }
        comparisonIds.add(comparison.id);
    });
});
export const EexConnectionUpdateRequestSchema = z.object({
    token: z.string().trim().min(1).max(4096).optional(),
    tokenExpiresAt: z.string().datetime({ offset: true }).nullable().optional(),
    enabled: z.boolean().optional(),
    clear: z.boolean().optional(),
}).strict();
export const EexWatchlistUpdateRequestSchema = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    enabled: z.boolean(),
}).strict();
export const EexStoragePreferenceUpdateRequestSchema = z.discriminatedUnion("storageMode", [
    z.object({
        scopeId: z.string().uuid(),
        subjectId: z.string().uuid(),
        storageMode: z.literal("live_only"),
    }).strict(),
    z.object({
        scopeId: z.string().uuid(),
        subjectId: z.string().uuid(),
        storageMode: z.literal("snapshots"),
        priorityLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    }).strict(),
]);
export const EexLiveUniverseUpdateRequestSchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("legacy_scopes"),
        expectedVersion: NonNegativeInteger,
    }).strict(),
    z.object({
        mode: z.literal("company_watchlist"),
        expectedVersion: NonNegativeInteger,
        subjectIds: z.array(z.string().uuid()).max(20_000),
    }).strict(),
]);
export const EexLiveUniverseRollingDeliveryKindSchema = z.enum([
    "day",
    "weekend",
    "week",
    "month",
    "quarter",
    "season",
    "year",
]);
const EexLiveUniverseRollingSeriesFields = {
    seriesKey: z.string().trim().min(1).max(320),
    seriesLabel: z.string().trim().min(1).max(240),
    commodityCode: z.string().trim().min(1).max(80),
    areaCode: z.string().trim().min(1).max(80),
    profile: z.string().trim().min(1).max(120).nullable(),
    familyCode: z.string().trim().min(1).max(120).nullable(),
    currency: z.string().trim().min(1).max(40).nullable(),
    uom: z.string().trim().min(1).max(80).nullable(),
};
export const EexLiveUniverseRollingLadderSchema = z.object({
    ...EexLiveUniverseRollingSeriesFields,
    deliveryKind: EexLiveUniverseRollingDeliveryKindSchema,
    selectionRule: z.enum(["nearest_n", "all_active"]),
    targetCount: z.number().int().min(1).max(5_000).nullable(),
}).strict().superRefine((value, ctx) => {
    if (value.selectionRule === "nearest_n" && value.targetCount == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["targetCount"],
            message: "nearest_n requires targetCount",
        });
    }
    if (value.selectionRule === "all_active" && value.targetCount != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["targetCount"],
            message: "all_active does not accept targetCount",
        });
    }
});
export const EexLiveUniverseRollingOverrideSchema = z.object({
    subjectId: z.string().uuid(),
    action: z.enum(["include", "exclude"]),
}).strict();
export const EexLiveUniverseRollingPolicyUpdateRequestSchema = z.object({
    expectedVersion: NonNegativeInteger,
    ladders: z.array(EexLiveUniverseRollingLadderSchema).min(1).max(3_500),
    overrides: z.array(EexLiveUniverseRollingOverrideSchema).max(20_000),
}).strict().superRefine((value, ctx) => {
    const ladderKeys = new Set();
    value.ladders.forEach((ladder, index) => {
        const key = `${ladder.seriesKey}\u0000${ladder.deliveryKind}`;
        if (ladderKeys.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["ladders", index],
                message: "duplicate rolling ladder",
            });
        }
        ladderKeys.add(key);
    });
    const overrideIds = new Set();
    value.overrides.forEach((override, index) => {
        if (overrideIds.has(override.subjectId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["overrides", index],
                message: "duplicate rolling override",
            });
        }
        overrideIds.add(override.subjectId);
    });
});
const EexLiveUniverseRollingSeriesSchema = z.object({
    ...EexLiveUniverseRollingSeriesFields,
    available: z.array(z.object({
        deliveryKind: EexLiveUniverseRollingDeliveryKindSchema,
        eligibleCount: NonNegativeInteger,
        selectedCount: NonNegativeInteger,
    }).strict()),
}).strict();
const EexLiveUniverseRollingMembershipSchema = z.object({
    subjectId: z.string().uuid(),
    seriesKey: z.string().min(1).nullable(),
    selected: z.boolean(),
    selectionReason: z.enum(["exact", "rolling", "included"]).nullable(),
    overrideAction: z.enum(["include", "exclude"]).nullable(),
}).strict();
export const EexLiveUniverseRollingPolicyResponseSchema = z.object({
    strategy: z.enum(["exact", "rolling"]),
    version: NonNegativeInteger,
    materializationVersion: NonNegativeInteger,
    resolutionStatus: z.enum(["pending", "resolved", "blocked"]),
    resolutionFailureReason: z.enum([
        "catalogue_incomplete",
        "configured_series_unavailable",
        "identity_collision",
        "selection_limit_exceeded",
    ]).nullable(),
    lastResolutionAttemptAt: NullableString,
    lastResolvedAt: NullableString,
    lastCatalogueCompletedAt: NullableString,
    ladders: z.array(EexLiveUniverseRollingLadderSchema),
    overrides: z.array(EexLiveUniverseRollingOverrideSchema),
    series: z.array(EexLiveUniverseRollingSeriesSchema),
    memberships: z.array(EexLiveUniverseRollingMembershipSchema),
    resolution: z.object({
        selectedInstruments: NonNegativeInteger,
        rollingInstruments: NonNegativeInteger,
        includedInstruments: NonNegativeInteger,
        excludedInstruments: NonNegativeInteger,
        deficits: z.array(z.object({
            seriesKey: z.string().min(1),
            deliveryKind: EexLiveUniverseRollingDeliveryKindSchema,
            targetCount: z.number().int().min(1).max(5_000),
            selectedCount: NonNegativeInteger,
        }).strict()),
    }).strict(),
}).strict();
export const EexConnectionStatusSchema = z.object({
    lifecycle: z.enum([
        "unconfigured",
        "stored_disabled",
        "validated_disabled",
        "enabled",
        "degraded",
        "legacy_reseal_required",
    ]),
    enabled: z.boolean(),
    configured: z.boolean(),
    pendingReplacement: z.boolean(),
    last4: NullableString,
    pendingLast4: NullableString,
    activeTokenExpiresAt: NullableString,
    pendingTokenExpiresAt: NullableString,
    activeExpiryState: z.enum(["unknown", "valid", "expiring_soon", "expired"]),
    pendingExpiryState: z.enum(["unknown", "valid", "expiring_soon", "expired"]),
    credentialWarning: NullableString,
    quotaPrincipalAssigned: z.boolean(),
    quotaPrincipalAttested: z.boolean(),
    pendingQuotaPrincipalAssigned: z.boolean(),
    pendingQuotaPrincipalAttested: z.boolean(),
    testInProgress: z.boolean(),
    pendingTestInProgress: z.boolean(),
    canEnable: z.boolean(),
    enablementBlockReason: NullableString,
    lastTestedAt: NullableString,
    lastTestStatus: NullableString,
    lastTestError: NullableString,
    pendingLastTestedAt: NullableString,
    pendingLastTestStatus: NullableString,
    pendingLastTestError: NullableString,
    updatedAt: NullableString,
}).strict();
export const EexConnectionResponseSchema = z.object({
    masterEnabled: z.boolean(),
    workerAvailable: z.boolean(),
    credentialStorageAvailable: z.boolean(),
    apiVersion: z.literal("multi-family"),
    baseUrl: z.string().url(),
    status: EexConnectionStatusSchema,
    canEdit: z.boolean(),
}).strict();
export const EexConnectionMutationResponseSchema = z.object({
    status: EexConnectionStatusSchema,
    masterEnabled: z.boolean(),
    workerAvailable: z.boolean(),
    credentialStorageAvailable: z.boolean(),
}).strict();
export const EexConnectionStatusOnlyResponseSchema = z.object({
    status: EexConnectionStatusSchema,
}).strict();
export const EexOperationalHealthResponseSchema = z.object({
    masterEnabled: z.boolean(),
    workerAvailable: z.boolean(),
    capabilities: z.array(z.object({
        credentialSlot: z.enum(["active", "pending"]),
        apiFamily: ApiFamily,
        state: z.enum(["unknown", "available", "degraded", "revoked"]),
        count: NonNegativeInteger,
    }).strict()),
    queue: z.object({
        recentWindowHours: z.number().positive(),
        counts: z.array(z.object({
            jobType: z.enum(["connection_test", "capability_validation", "catalogue_sync", "acquisition"]),
            apiFamily: ApiFamily,
            state: z.enum(["queued", "leased", "completed", "failed", "cancelled"]),
            count: NonNegativeInteger,
        }).strict()),
    }).strict(),
}).strict();
export const EexMarketDataScopesResponseSchema = z.object({
    asOf: NonEmptyString,
    scopes: z.array(z.object({
        id: z.string().uuid(),
        apiFamily: ApiFamily,
        marketSegment: NonEmptyString,
        commodityCode: NonEmptyString,
        areaCode: NonEmptyString,
        dataset: NonEmptyString,
        selectorType: NonEmptyString,
        selectorCode: NullableString,
        livePollIntervalSeconds: NonNegativeInteger.nullable(),
        snapshotPriority: z.number().int().min(1).max(3).nullable(),
        rolloutPhase: z.enum(["shadow", "live_readonly", "history_enabled", "quote_enabled"]).nullable(),
        rolloutGatePresent: z.boolean(),
        companySelectionViewOnly: z.boolean(),
    }).strict()),
    filters: z.object({
        apiFamilies: z.array(ApiFamily),
        marketSegments: z.array(z.string()),
        commodityCodes: z.array(z.string()),
        areaCodes: z.array(z.string()),
        datasets: z.array(z.string()),
    }).strict(),
}).strict();
export const EexMarketDataFeedHealthItemSchema = z.object({
    scopeId: z.string().uuid(),
    apiFamily: ApiFamily,
    marketSegment: NonEmptyString,
    commodityCode: NonEmptyString,
    areaCode: NonEmptyString,
    dataset: NonEmptyString,
    selectorType: NonEmptyString,
    selectorCode: NullableString,
    state: z.enum(["fresh", "stale", "disconnected", "degraded"]),
    displayState: z.enum(["live", "stale", "disconnected", "partial", "degraded"]),
    lastAttemptedAt: NullableString,
    lastSuccessfulAt: NullableString,
    latestMarketTimestamp: NullableString,
    returnedRowCount: NonNegativeInteger.nullable(),
    expectedRowCount: NonNegativeInteger.nullable(),
    catalogueSubjectCount: NonNegativeInteger.nullable(),
    coverageAssessment: z.enum(["complete", "partial", "unknown"]),
    schemaDriftCount: NonNegativeInteger,
    rateLimitCount: NonNegativeInteger,
    lastIssueAt: NullableString,
    heartbeatAt: NullableString,
    circuitState: z.enum(["closed", "open", "half_open"]),
    warnings: z.array(z.enum(["partial_coverage", "schema_drift", "stale", "disconnected", "degraded"])),
}).strict();
export const EexMarketDataFeedHealthResponseSchema = z.object({
    asOf: NonEmptyString,
    items: z.array(EexMarketDataFeedHealthItemSchema),
}).strict();
export const EexMarketDataInstrumentSchema = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    apiFamily: MarketFamily,
    providerKey: NonEmptyString,
    commodityCode: NonEmptyString,
    areaCode: NonEmptyString,
    selectorCode: NullableString,
    displayName: NullableString,
    productGroup: ProductGroup.nullable(),
    deliveryPeriod: DeliveryPeriod.nullable(),
    instrumentType: NullableString,
    productType: NullableString,
    maturityType: NullableString,
    deliveryStart: NullableString,
    deliveryEnd: NullableString,
    currency: NullableString,
    uom: NullableString,
    active: z.boolean(),
    personalWatchlisted: z.boolean(),
    companyWatchlisted: z.boolean(),
}).strict();
export const EexMarketDataInstrumentsResponseSchema = z.object({
    asOf: NonEmptyString,
    items: z.array(EexMarketDataInstrumentSchema),
    page: Page,
}).strict();
export const EexMarketDataTopOfBookItemSchema = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    apiFamily: MarketFamily,
    commodityCode: NonEmptyString,
    areaCode: NonEmptyString,
    providerKey: NonEmptyString,
    displayName: NullableString,
    productGroup: ProductGroup.nullable(),
    deliveryPeriod: DeliveryPeriod.nullable(),
    marketTimestamp: NullableString,
    receivedAt: NonEmptyString,
    bidPrice: NullableString,
    bidLots: NullableString,
    bidVolume: NullableString,
    bidType: NullableString,
    askPrice: NullableString,
    askLots: NullableString,
    askVolume: NullableString,
    askType: NullableString,
    midpoint: NullableString,
    lastTradePrice: NullableString,
    lastTradeLots: NullableString,
    lastTradeVolume: NullableString,
    lastTradeAt: NullableString,
    lastTradeReceivedAt: NullableString,
    lastTradeSourceScopeId: z.string().uuid().nullable(),
    lastTradeSourceDataset: z.literal("public_trades").nullable(),
    settlementPrice: NullableString.optional(),
    settlementTradingDate: NullableString.optional(),
    settlementCurrency: NullableString.optional(),
    settlementUom: NullableString.optional(),
    settlementReceivedAt: NullableString.optional(),
    settlementSource: z.enum(["eex_datasource", "ecc_span"]).nullable().optional(),
    freshnessState: z.enum(["fresh", "stale", "disconnected"]),
    coverageState: z.enum(["unknown", "quoted", "unquoted", "omitted"]),
    personalWatchlisted: z.boolean(),
    companyWatchlisted: z.boolean(),
    storageMode: z.enum(["live_only", "snapshots", "publication_events"]).nullable(),
    priorityLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
    snapshotIntervalSeconds: z.union([z.literal(300), z.literal(900), z.literal(3600)]).nullable(),
}).strict();
export const EexMarketDataTopOfBookResponseSchema = z.object({
    asOf: NonEmptyString,
    items: z.array(EexMarketDataTopOfBookItemSchema),
    page: Page,
}).strict();
export const EexMarketDataSettlementMarkSchema = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    apiFamily: MarketFamily,
    commodityCode: NonEmptyString,
    areaCode: NonEmptyString,
    providerKey: NonEmptyString,
    displayName: NullableString,
    productGroup: ProductGroup.nullable(),
    deliveryPeriod: DeliveryPeriod.nullable(),
    price: NonEmptyString,
    tradingDate: NonEmptyString,
    currency: NullableString,
    uom: NullableString,
    receivedAt: NonEmptyString,
    source: z.enum(["eex_datasource", "ecc_span"]),
}).strict();
export const EexMarketDataSettlementMarksResponseSchema = z.object({
    asOf: NonEmptyString,
    items: z.array(EexMarketDataSettlementMarkSchema),
    page: Page,
}).strict();
export const EexMarketDataForwardCurveSnapshotPointSchema = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    snapshotBoundary: NullableString,
    priorityLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
    marketTimestamp: NullableString,
    receivedAt: NullableString,
    bidPrice: NullableString,
    askPrice: NullableString,
    midpoint: NullableString,
    lastTradePrice: NullableString,
    lastTradeAt: NullableString,
    lastTradeReceivedAt: NullableString,
    freshnessState: z.enum(["fresh", "stale", "disconnected"]).nullable(),
    coverageState: z.enum(["unknown", "quoted", "unquoted", "omitted"]).nullable(),
    gapReason: z.literal("no_snapshot_before_target").nullable(),
}).strict();
export const EexMarketDataForwardCurveSnapshotSeriesSchema = z.object({
    id: NonEmptyString,
    requestedAt: z.string().datetime({ offset: true }),
    points: z.array(EexMarketDataForwardCurveSnapshotPointSchema)
        .max(EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX),
}).strict();
export const EexMarketDataForwardCurveSettlementPointSchema = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    price: NullableString,
    currency: NullableString,
    uom: NullableString,
    receivedAt: NullableString,
    source: z.enum(["eex_datasource", "ecc_span"]).nullable(),
    gapReason: z.literal("no_settlement_for_date").nullable(),
}).strict();
export const EexMarketDataForwardCurveResponseSchema = z.object({
    asOf: z.string().datetime({ offset: true }),
    snapshotSeries: z.array(EexMarketDataForwardCurveSnapshotSeriesSchema)
        .max(EEX_MARKET_DATA_FORWARD_CURVE_SNAPSHOT_MAX),
    previousSettlement: z.object({
        tradingDate: z.string().date().nullable(),
        points: z.array(EexMarketDataForwardCurveSettlementPointSchema)
            .max(EEX_MARKET_DATA_FORWARD_CURVE_SUBJECT_MAX),
    }).strict(),
}).strict();
export const EexMarketDataPublicTradeSchema = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    apiFamily: MarketFamily,
    commodityCode: NonEmptyString,
    areaCode: NonEmptyString,
    providerKey: NonEmptyString,
    displayName: NullableString,
    tradeKey: NonEmptyString,
    eventKey: NonEmptyString,
    providerTradeId: NonEmptyString,
    providerVersion: NullableString,
    rawUpdateAction: NullableString,
    tradeType: NullableString,
    aggressorAction: NullableString,
    price: NullableString,
    lots: NullableString,
    volume: NullableString,
    marketTimestamp: NullableString,
    otcTimestamp: NullableString,
    volumeOnly: z.boolean().nullable(),
    deleted: z.boolean(),
    receivedAt: NonEmptyString,
    ingestionSequence: NonEmptyString,
}).strict();
export const EexMarketDataPublicTradesResponseSchema = z.object({
    asOf: NonEmptyString,
    mode: z.enum(["current", "events"]),
    items: z.array(EexMarketDataPublicTradeSchema),
    page: Page,
}).strict();
export const EexSubjectPreferenceSchema = z.object({
    scopeId: z.string().uuid(),
    subjectId: z.string().uuid(),
    apiFamily: ApiFamily,
    commodityCode: NonEmptyString,
    areaCode: NonEmptyString,
    personalWatchlistConfigured: z.boolean(),
    personalWatchlisted: z.boolean(),
    companyWatchlistConfigured: z.boolean(),
    companyWatchlisted: z.boolean(),
    watchlistConfigured: z.boolean(),
    watchlisted: z.boolean(),
    storageMode: z.enum(["live_only", "snapshots", "publication_events"]).nullable(),
    priorityLevel: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
    snapshotIntervalSeconds: z.union([z.literal(300), z.literal(900), z.literal(3600)]).nullable(),
}).strict();
export const EexSubjectPreferenceMutationResponseSchema = z.object({
    preference: EexSubjectPreferenceSchema,
}).strict();
const LiveUniverseItem = z.object({
    subjectId: z.string().uuid(),
    providerKey: NonEmptyString,
    displayName: NullableString,
    commodityCode: NonEmptyString,
    areaCode: NonEmptyString,
    instrumentType: NullableString,
    productType: NullableString,
    maturityType: NullableString,
    currency: NullableString,
    uom: NullableString,
    productGroup: ProductGroup,
    deliveryPeriod: DeliveryPeriod,
    topOfBookScopeId: z.string().uuid().nullable(),
    publicTradesScopeId: z.string().uuid().nullable(),
    selected: z.boolean(),
    areaFeedActive: z.boolean(),
}).strict();
export const EexLiveUniverseResponseSchema = z.object({
    configured: z.boolean(),
    mode: z.enum(["legacy_scopes", "company_watchlist"]),
    version: NonNegativeInteger,
    configuredAt: NullableString,
    updatedAt: NullableString,
    items: z.array(LiveUniverseItem),
    summary: z.object({
        eligibleInstruments: NonNegativeInteger,
        selectedInstruments: NonNegativeInteger,
        eligibleAreaFeeds: NonNegativeInteger,
        activeAreaFeeds: NonNegativeInteger,
    }).strict(),
    reconciliation: z.object({
        activatedAreaFeeds: NonNegativeInteger,
        deactivatedAreaFeeds: NonNegativeInteger,
        cancelledQueuedJobs: NonNegativeInteger,
        resetPublicTradeCursors: NonNegativeInteger,
        activationJobsQueued: NonNegativeInteger,
        activationQueueWarning: NullableString,
    }).strict().optional(),
}).strict();
//# sourceMappingURL=eex-market-data-contracts.js.map